// 📊 src/pages/Audience.jsx
//
// Who is using Oráku and on what device. Aggregates the users collection by
// PLATFORM (iPhone / Android / Web), by PLAN (free vs paid), and by COUNTRY,
// and renders charts so we can see, at a glance:
//   - how many iPhone vs Android vs Web users we have
//   - the free vs paid split, overall and per platform
//   - where users are (top countries)
//
// Platform is derived in platformOf(): explicit `platform` field wins, else the
// in-app-purchase platform (apple→iOS, google→Android). Mobile free users start
// as "Unknown" until the next app build stamps `platform`; web + paid users show
// immediately.

import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { PageHeader, StatCard, Card, Spinner } from '../components/UI';
import { platformOf, PLATFORM_META } from './Users';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

const ACCENT = '#008751';
const GOLD = '#D4AF37';
const FETCH_LIMIT = 2000;

const CHART_TOOLTIP = {
  contentStyle: { background: '#1A2332', border: '1px solid rgba(0,135,81,0.2)', borderRadius: 10, fontSize: 12 },
  itemStyle: { color: '#fff' }, labelStyle: { color: 'rgba(255,255,255,0.5)', marginBottom: 4 },
};
const TIER_LABEL = { free: 'Free', student: 'Student', premium: 'Oga', scholar: 'Odogwu', family: 'Family', school: 'School' };
const isPaid = (t) => t && t !== 'free';

export default function Audience() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { (async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, 'users'), orderBy('createdAt', 'desc'), limit(FETCH_LIMIT)));
      setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error('Audience load failed:', e); }
    setLoading(false);
  })(); }, []);

  const stats = useMemo(() => {
    const platform = { ios: 0, android: 0, web: 0, unknown: 0 };
    const tier = {};
    const byPlatformPlan = {}; // platform → {free, paid}
    const country = new Map();
    let free = 0, paid = 0;

    for (const u of users) {
      const p = platformOf(u);
      platform[p] = (platform[p] || 0) + 1;
      const t = u.subscriptionTier || 'free';
      tier[t] = (tier[t] || 0) + 1;
      const plan = isPaid(t) ? 'paid' : 'free';
      if (plan === 'paid') paid++; else free++;
      byPlatformPlan[p] = byPlatformPlan[p] || { free: 0, paid: 0 };
      byPlatformPlan[p][plan]++;
      if (u.country) country.set(u.country, (country.get(u.country) || 0) + 1);
    }

    const platformData = ['ios', 'android', 'web', 'unknown']
      .filter((k) => platform[k] > 0)
      .map((k) => ({ name: PLATFORM_META[k].label, value: platform[k], color: PLATFORM_META[k].color, key: k }));

    const planByPlatform = ['ios', 'android', 'web', 'unknown']
      .filter((k) => (byPlatformPlan[k]?.free || 0) + (byPlatformPlan[k]?.paid || 0) > 0)
      .map((k) => ({ name: PLATFORM_META[k].label, Free: byPlatformPlan[k].free, Paid: byPlatformPlan[k].paid }));

    const tierData = Object.entries(tier)
      .map(([k, v]) => ({ name: TIER_LABEL[k] || k, value: v }))
      .sort((a, b) => b.value - a.value);

    const countryData = [...country.entries()]
      .map(([k, v]) => ({ name: k, value: v }))
      .sort((a, b) => b.value - a.value).slice(0, 8);

    return { platform, platformData, planByPlatform, tierData, countryData, free, paid, total: users.length };
  }, [users]);

  if (loading) return <div style={{ padding: 40 }}><Spinner /></div>;

  return (
    <div style={{ padding: 32 }}>
      <PageHeader title="Audience" subtitle={`${stats.total.toLocaleString()} users · by platform, plan and country`} />

      {/* KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Total Users" value={stats.total.toLocaleString()} color={ACCENT} />
        <StatCard label="iPhone (iOS)" value={stats.platform.ios.toLocaleString()} color={PLATFORM_META.ios.color} />
        <StatCard label="Android" value={stats.platform.android.toLocaleString()} color={PLATFORM_META.android.color} />
        <StatCard label="Web" value={stats.platform.web.toLocaleString()} color={GOLD} />
        <StatCard label="Paid Users" value={stats.paid.toLocaleString()} sub={`${pct(stats.paid, stats.total)}% of total`} color="#a855f7" />
        <StatCard label="Free Users" value={stats.free.toLocaleString()} sub={`${pct(stats.free, stats.total)}% of total`} color="rgba(255,255,255,0.5)" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20, marginBottom: 20 }}>
        {/* Platform pie */}
        <Card>
          <H>Users by Platform</H>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={stats.platformData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50} paddingAngle={2}>
                {stats.platformData.map((e) => <Cell key={e.key} fill={e.color} />)}
              </Pie>
              <Tooltip {...CHART_TOOLTIP} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        {/* Free vs Paid per platform */}
        <Card>
          <H>Free vs Paid by Platform</H>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.planByPlatform}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="name" stroke="rgba(255,255,255,0.6)" fontSize={11} />
              <YAxis stroke="rgba(255,255,255,0.6)" fontSize={11} allowDecimals={false} />
              <Tooltip {...CHART_TOOLTIP} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Free" stackId="a" fill="rgba(255,255,255,0.35)" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Paid" stackId="a" fill="#a855f7" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20 }}>
        {/* Tier breakdown */}
        <Card>
          <H>Users by Plan</H>
          <ResponsiveContainer width="100%" height={Math.max(220, 42 * stats.tierData.length)}>
            <BarChart data={stats.tierData} layout="vertical" margin={{ left: 10, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis type="number" stroke="rgba(255,255,255,0.6)" fontSize={11} allowDecimals={false} />
              <YAxis type="category" dataKey="name" stroke="rgba(255,255,255,0.7)" fontSize={11} width={70} />
              <Tooltip {...CHART_TOOLTIP} />
              <Bar dataKey="value" fill={GOLD} radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Where (top countries) */}
        <Card>
          <H>Where Users Are (Top Countries)</H>
          {stats.countryData.length === 0
            ? <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, padding: 20 }}>Location fills in as users chat (geo is stamped server-side).</div>
            : (
              <ResponsiveContainer width="100%" height={Math.max(220, 32 * stats.countryData.length + 40)}>
                <BarChart data={stats.countryData} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis type="number" stroke="rgba(255,255,255,0.6)" fontSize={11} allowDecimals={false} />
                  <YAxis type="category" dataKey="name" stroke="rgba(255,255,255,0.7)" fontSize={11} width={50} />
                  <Tooltip {...CHART_TOOLTIP} />
                  <Bar dataKey="value" fill={ACCENT} radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
        </Card>
      </div>

      {stats.platform.unknown > 0 && (
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, marginTop: 18, lineHeight: 1.5 }}>
          {stats.platform.unknown.toLocaleString()} users show as "Unknown" platform — these are existing mobile users from before
          device tracking shipped. They are tagged as iPhone or Android automatically the next time they open the updated app.
          Need a specific user's chat history for a warrant? Open them in <b>Users</b> → <b>Download Conversations (Warrant)</b>.
        </p>
      )}
    </div>
  );
}

function H({ children }) {
  return <h3 style={{ color: '#fff', fontSize: 14, fontWeight: 700, marginBottom: 12, letterSpacing: 0.5, textTransform: 'uppercase' }}>{children}</h3>;
}
const pct = (n, total) => (total ? Math.round((n / total) * 100) : 0);
